import {snakeCase} from 'lodash';
import axiosWithAuth from '@/utils/axiosWithAuth';
import mapKeysToCase from '@/utils/mapKeysToCase';
import type {
  CreateSegmentRequest,
  Segment,
  UpdateSegmentRequest,
} from '@/types/models/segments/segment';

const SEGMENTS_PATH = 'segments';

/**
 * List segments for the given merchant. Requires merchant_id (use brandId from useBrandId).
 */
export async function listSegments(merchantId: string): Promise<Segment[]> {
  const {data} = await axiosWithAuth.get<Segment[]>(SEGMENTS_PATH, {
    params: {merchant_id: merchantId},
  });
  return data;
}

/**
 * Get a single segment by id. Requires merchant_id (use brandId from useBrandId).
 */
export async function getSegment(
  id: string,
  merchantId: string
): Promise<Segment> {
  const {data} = await axiosWithAuth.get<Segment>(`${SEGMENTS_PATH}/${id}`, {
    params: {merchant_id: merchantId},
  });
  return data;
}

/**
 * Create a segment. Request body is sent in snake_case.
 */
export async function createSegment(
  payload: CreateSegmentRequest
): Promise<Segment> {
  const body = mapKeysToCase(payload, snakeCase);
  const {data} = await axiosWithAuth.post<Segment>(SEGMENTS_PATH, body);
  return data;
}

/**
 * Update a segment. Request body is sent in snake_case.
 */
export async function updateSegment(
  id: string,
  payload: UpdateSegmentRequest
): Promise<Segment> {
  const body = mapKeysToCase(payload, snakeCase);
  const {data} = await axiosWithAuth.put<Segment>(
    `${SEGMENTS_PATH}/${id}`,
    body
  );
  return data;
}

/**
 * Delete a segment. Requires merchant_id (use brandId from useBrandId).
 */
export async function deleteSegment(
  id: string,
  merchantId: string
): Promise<void> {
  await axiosWithAuth.delete(`${SEGMENTS_PATH}/${id}`, {
    params: {merchant_id: merchantId},
  });
}

const segmentsFetchers = {
  listSegments,
  getSegment,
  createSegment,
  updateSegment,
  deleteSegment,
};

export default segmentsFetchers;
